import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { CheckCircle, ExternalLink, Download } from 'lucide-react';

const grainTypes = [
  {
    name: 'Barley',
    image: 'https://images.pexels.com/photos/5538161/pexels-photo-5538161.jpeg'
  },
  {
    name: 'Wheat',
    image: 'https://images.pexels.com/photos/54084/wheat-grain-agriculture-seed-54084.jpeg'
  },
  {
    name: 'Oats',
    image: 'https://images.unsplash.com/photo-1571509097233-d5edced62422?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxvYXRzJTIwZ3JhaW58ZW58MHx8fHwxNzYyOTkwNTQ5fDA&ixlib=rb-4.1.0&q=85'
  },
  {
    name: 'Lentils',
    image: 'https://images.unsplash.com/photo-1552585960-0e1069ce7405?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsZW50aWxzfGVufDB8fHx8MTc2Mjk5MDU1NHww&ixlib=rb-4.1.0&q=85'
  }
];

const additionalCommodities = ['Peas', 'Beans', 'Canola', 'Chickpeas', 'And More'];

const HarvestPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-[#1a1a1a] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Harvest 2025/26</h1>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Preparing for another successful harvest season with reliable storage and export solutions.
            </p>
          </div>
        </section>

        {/* Planning Ahead Banner */}
        <section className="py-8 bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-800">
              <strong>Planning ahead for Harvest 2025/26?</strong> Contact us now to discuss your storage requirements and secure your capacity.
            </p>
          </div>
        </section>

        {/* Grains We Handle */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Grains We Handle This Harvest</h2>
            <p className="text-gray-600 text-center mb-12">
              We're equipped to receive and store a wide variety of grains and commodities for the 2025/26 season.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              {grainTypes.map((grain, index) => (
                <div key={index} className="text-center">
                  <div className="aspect-square rounded-lg overflow-hidden mb-3">
                    <img
                      src={grain.image}
                      alt={`${grain.name} grain`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900">{grain.name}</h3>
                </div>
              ))}
            </div>

            <div className="bg-[#faf8f5] rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Additional Commodities</h3>
              <p className="text-gray-700">Following harvest, we also handle a complete range of commodities including:</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {additionalCommodities.map((item, index) => (
                  <span key={index} className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-gray-200">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Season Preparation */}
        <section className="py-16 bg-[#faf8f5]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <h3 className="font-bold text-gray-900 mb-3">Season Preparation</h3>
                <p className="text-gray-600 text-sm">
                  Our facilities are being prepared and maintained to ensure optimal storage conditions for the upcoming harvest season.
                </p>
              </div>
              <div className="text-center p-6">
                <h3 className="font-bold text-gray-900 mb-3">Storage Capacity</h3>
                <p className="text-gray-600 text-sm">
                  We offer flexible storage solutions to accommodate various grain types and volumes, with modern facilities designed for quality preservation.
                </p>
              </div>
              <div className="text-center p-6">
                <h3 className="font-bold text-gray-900 mb-3">Export Services</h3>
                <p className="text-gray-600 text-sm">
                  Our container packing services are ready to support your export needs, with experienced staff and efficient processes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Harvest Services */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Harvest Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Grain Receival</h3>
                <p className="text-gray-600 text-sm">
                  Efficient receival processes to minimize your wait time during the busy harvest period. Our team is committed to providing prompt, professional service.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Quality Storage</h3>
                <p className="text-gray-600 text-sm">
                  Modern storage facilities equipped to maintain grain quality throughout the storage period. We implement strict protocols to protect your investment.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">Container Packing</h3>
                <p className="text-gray-600 text-sm">
                  Professional container packing services at our Wycheproof site, supporting your export opportunities with careful handling and documentation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Storage Agreement */}
        <section className="py-16 bg-[#faf8f5]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Storage & Handling Agreement 2025/26</h2>
            <p className="text-gray-700 mb-6">
              Review our comprehensive storage and handling agreement outlining terms, conditions, and fees for the 2025/26 harvest season. This agreement covers receival, storage, testing, outturn, and all related services.
            </p>
            <a
              href="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/5piol1vx_Wyche%20Grain%20Agreement%202025-26%20season.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-yellow-500 text-gray-900 font-semibold rounded-md hover:bg-yellow-400 transition-all duration-300"
            >
              <Download size={18} />
              Download Agreement (PDF)
            </a>
            <p className="text-sm text-gray-500 mt-4">
              Agreement period: November 1, 2025 - October 31, 2026
            </p>
          </div>
        </section>

        {/* Weather Link */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Stay Informed</h2>
            <p className="text-gray-700 mb-6">
              Keep track of weather conditions to make informed decisions about your harvest timing.
            </p>
            <a
              href="https://www.weatherzone.com.au/vic/mallee/wycheproof"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-yellow-500 text-yellow-600 font-semibold rounded-md hover:bg-yellow-500 hover:text-gray-900 transition-all duration-300"
            >
              <ExternalLink size={18} />
              Check Wycheproof Weather
            </a>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#1a1a1a]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Book Your Storage?</h2>
            <p className="text-gray-300 mb-8">
              Contact our team to discuss your requirements for the 2025/26 harvest season.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-3 bg-yellow-500 text-gray-900 font-semibold rounded-md hover:bg-yellow-400 transition-all duration-300"
            >
              Get In Touch
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HarvestPage;
